import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { adminApi } from '../services/api';

interface EntitlementLimit {
  entitlement_key: string;
  notes_storage_mb: number;
  notes_max_upload_mb: number;
  notes_media_sync_enabled: boolean;
  notes_large_media_enabled: boolean;
}

interface ProductEntry {
  product_id: string;
  entitlement_key: string;
  duration_days: number;
  platform: 'ios' | 'android' | 'web';
  active: boolean;
  isNew?: boolean;
}

interface UserEntitlementEntry {
  entitlement_key: string;
  status: 'active' | 'grace' | 'expired' | 'revoked';
  expires_at: string;
  isNew?: boolean;
}

interface UserOverrideEntry {
  entitlement_key: string;
  notes_storage_mb_override: string;
  notes_max_upload_mb_override: string;
  notes_media_sync_enabled_override: 'inherit' | 'true' | 'false';
  notes_large_media_enabled_override: 'inherit' | 'true' | 'false';
  isNew?: boolean;
}

const emptyProduct: ProductEntry = {
  product_id: '',
  entitlement_key: '',
  duration_days: 365,
  platform: 'ios',
  active: true,
  isNew: true,
};

const emptyUserEntitlement: UserEntitlementEntry = {
  entitlement_key: '',
  status: 'active',
  expires_at: '',
  isNew: true,
};

const emptyUserOverride: UserOverrideEntry = {
  entitlement_key: '',
  notes_storage_mb_override: '',
  notes_max_upload_mb_override: '',
  notes_media_sync_enabled_override: 'inherit',
  notes_large_media_enabled_override: 'inherit',
  isNew: true,
};

export default function BillingSettings() {
  const [limits, setLimits] = useState<EntitlementLimit[]>([]);
  const [products, setProducts] = useState<ProductEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [userIdInput, setUserIdInput] = useState('');
  const [userEntitlements, setUserEntitlements] = useState<UserEntitlementEntry[]>([]);
  const [userOverrides, setUserOverrides] = useState<UserOverrideEntry[]>([]);
  const [userLoading, setUserLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [limitsRes, productsRes] = await Promise.all([
        adminApi.getEntitlementLimits(),
        adminApi.getBillingProducts(),
      ]);
      setLimits(limitsRes.data.limits || []);
      setProducts(productsRes.data.products || []);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const toLocalInput = (value?: string | null) => {
    if (!value) return '';
    return value.replace('Z', '').slice(0, 16);
  };

  const parseOptionalInt = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = parseInt(trimmed, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const parseOverrideBool = (value: 'inherit' | 'true' | 'false') => {
    if (value === 'inherit') return null;
    return value === 'true';
  };

  const loadUserData = async () => {
    const userId = parseInt(userIdInput, 10);
    if (!userId) {
      toast.error('Enter a valid user ID');
      return;
    }
    try {
      setUserLoading(true);
      const [entRes, overrideRes] = await Promise.all([
        adminApi.getUserEntitlements(userId),
        adminApi.getUserOverrides(userId),
      ]);
      const entitlements = (entRes.data.entitlements || []).map((item: any) => ({
        entitlement_key: item.entitlement_key ?? '',
        status: (item.status ?? 'active') as UserEntitlementEntry['status'],
        expires_at: toLocalInput(item.expires_at),
      }));
      const overrides = (overrideRes.data.overrides || []).map((item: any) => ({
        entitlement_key: item.entitlement_key ?? '',
        notes_storage_mb_override: item.notes_storage_mb_override?.toString() ?? '',
        notes_max_upload_mb_override: item.notes_max_upload_mb_override?.toString() ?? '',
        notes_media_sync_enabled_override:
          item.notes_media_sync_enabled_override === true
            ? 'true'
            : item.notes_media_sync_enabled_override === false
              ? 'false'
              : 'inherit',
        notes_large_media_enabled_override:
          item.notes_large_media_enabled_override === true
            ? 'true'
            : item.notes_large_media_enabled_override === false
              ? 'false'
              : 'inherit',
      }));
      setUserEntitlements(entitlements);
      setUserOverrides(overrides);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load user settings');
    } finally {
      setUserLoading(false);
    }
  };

  const updateLimit = async (limit: EntitlementLimit) => {
    try {
      setSavingKey(`limit-${limit.entitlement_key}`);
      await adminApi.updateEntitlementLimit(limit);
      toast.success('Entitlement limits updated');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update limits');
    } finally {
      setSavingKey(null);
    }
  };

  const updateProduct = async (product: ProductEntry) => {
    if (!product.product_id || !product.entitlement_key) {
      toast.error('Product ID and entitlement key are required');
      return;
    }
    try {
      setSavingKey(`product-${product.product_id}`);
      await adminApi.updateBillingProduct({
        product_id: product.product_id,
        entitlement_key: product.entitlement_key,
        duration_days: product.duration_days,
        platform: product.platform,
        active: product.active,
      });
      toast.success('Product mapping saved');
      await loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    } finally {
      setSavingKey(null);
    }
  };

  const saveUserEntitlement = async (entry: UserEntitlementEntry) => {
    const userId = parseInt(userIdInput, 10);
    if (!userId || !entry.entitlement_key) {
      toast.error('User ID and entitlement key are required');
      return;
    }
    try {
      setSavingKey(`user-entitlement-${entry.entitlement_key}`);
      await adminApi.updateUserEntitlement({
        user_id: userId,
        entitlement_key: entry.entitlement_key,
        status: entry.status,
        expires_at: entry.expires_at ? entry.expires_at : null,
      });
      toast.success('User entitlement updated');
      await loadUserData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update user entitlement');
    } finally {
      setSavingKey(null);
    }
  };

  const saveUserOverride = async (entry: UserOverrideEntry) => {
    const userId = parseInt(userIdInput, 10);
    if (!userId || !entry.entitlement_key) {
      toast.error('User ID and entitlement key are required');
      return;
    }
    try {
      setSavingKey(`user-override-${entry.entitlement_key}`);
      await adminApi.updateUserOverride({
        user_id: userId,
        entitlement_key: entry.entitlement_key,
        notes_storage_mb_override: parseOptionalInt(entry.notes_storage_mb_override),
        notes_max_upload_mb_override: parseOptionalInt(entry.notes_max_upload_mb_override),
        notes_media_sync_enabled_override: parseOverrideBool(entry.notes_media_sync_enabled_override),
        notes_large_media_enabled_override: parseOverrideBool(entry.notes_large_media_enabled_override),
      });
      toast.success('User override saved');
      await loadUserData();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update user override');
    } finally {
      setSavingKey(null);
    }
  };

  const updateLimitField = (
    key: string,
    field: keyof EntitlementLimit,
    value: string | number | boolean,
  ) => {
    setLimits((prev) =>
      prev.map((limit) =>
        limit.entitlement_key === key ? { ...limit, [field]: value } : limit,
      ),
    );
  };

  const updateProductField = (
    index: number,
    field: keyof ProductEntry,
    value: string | number | boolean,
  ) => {
    setProducts((prev) =>
      prev.map((product, idx) => (idx === index ? { ...product, [field]: value } : product)),
    );
  };

  const addProduct = () => {
    setProducts((prev) => [...prev, { ...emptyProduct }]);
  };

  const updateUserEntitlementField = (
    index: number,
    field: keyof UserEntitlementEntry,
    value: string,
  ) => {
    setUserEntitlements((prev) =>
      prev.map((entry, idx) => (idx === index ? { ...entry, [field]: value } : entry)),
    );
  };

  const updateUserOverrideField = (
    index: number,
    field: keyof UserOverrideEntry,
    value: string,
  ) => {
    setUserOverrides((prev) =>
      prev.map((entry, idx) => (idx === index ? { ...entry, [field]: value } : entry)),
    );
  };

  const addUserEntitlement = () => {
    setUserEntitlements((prev) => [...prev, { ...emptyUserEntitlement }]);
  };

  const addUserOverride = () => {
    setUserOverrides((prev) => [...prev, { ...emptyUserOverride }]);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <p className="text-gray-600 dark:text-gray-300">Loading billing settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Billing & Paywall</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Update storage tiers and RevenueCat product mappings without app changes.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notes storage tiers</h2>
        <div className="space-y-4">
          {limits.map((limit) => (
            <div
              key={limit.entitlement_key}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {limit.entitlement_key}
                </h3>
                <button
                  onClick={() => updateLimit(limit)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
                  disabled={savingKey === `limit-${limit.entitlement_key}`}
                >
                  {savingKey === `limit-${limit.entitlement_key}` ? 'Saving...' : 'Save'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Storage (MB)
                  <input
                    type="number"
                    value={limit.notes_storage_mb}
                    onChange={(e) =>
                      updateLimitField(
                        limit.entitlement_key,
                        'notes_storage_mb',
                        parseInt(e.target.value || '0', 10),
                      )
                    }
                    className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  />
                </label>
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Max upload (MB)
                  <input
                    type="number"
                    value={limit.notes_max_upload_mb}
                    onChange={(e) =>
                      updateLimitField(
                        limit.entitlement_key,
                        'notes_max_upload_mb',
                        parseInt(e.target.value || '0', 10),
                      )
                    }
                    className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={limit.notes_media_sync_enabled}
                    onChange={(e) =>
                      updateLimitField(
                        limit.entitlement_key,
                        'notes_media_sync_enabled',
                        e.target.checked,
                      )
                    }
                  />
                  Media sync enabled
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={limit.notes_large_media_enabled}
                    onChange={(e) =>
                      updateLimitField(
                        limit.entitlement_key,
                        'notes_large_media_enabled',
                        e.target.checked,
                      )
                    }
                  />
                  Large media enabled
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">RevenueCat product catalog</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Map store SKUs to entitlements so the app shows the right paywall options.
            </p>
          </div>
          <button
            onClick={addProduct}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm"
          >
            Add product
          </button>
        </div>
        <div className="mt-4 space-y-4">
          {products.map((product, index) => (
            <div
              key={`${product.product_id}-${index}`}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Product ID
                  <input
                    type="text"
                    value={product.product_id}
                    onChange={(e) => updateProductField(index, 'product_id', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  />
                </label>
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Entitlement
                  <input
                    type="text"
                    value={product.entitlement_key}
                    onChange={(e) => updateProductField(index, 'entitlement_key', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  />
                </label>
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Platform
                  <select
                    value={product.platform}
                    onChange={(e) =>
                      updateProductField(index, 'platform', e.target.value as ProductEntry['platform'])
                    }
                    className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  >
                    <option value="ios">iOS</option>
                    <option value="android">Android</option>
                    <option value="web">Web</option>
                  </select>
                </label>
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Duration (days)
                  <input
                    type="number"
                    value={product.duration_days}
                    onChange={(e) =>
                      updateProductField(index, 'duration_days', parseInt(e.target.value || '0', 10))
                    }
                    className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={product.active}
                    onChange={(e) => updateProductField(index, 'active', e.target.checked)}
                  />
                  Active
                </label>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => updateProduct(product)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
                  disabled={savingKey === `product-${product.product_id}`}
                >
                  {savingKey === `product-${product.product_id}` ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">User overrides</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Grant special access or custom storage limits for specific users.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3">
          <input
            type="number"
            placeholder="User ID"
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
            className="w-full md:w-64 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
          />
          <button
            onClick={loadUserData}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm"
          >
            {userLoading ? 'Loading...' : 'Load user'}
          </button>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 dark:text-white">User entitlements</h3>
            <button
              onClick={addUserEntitlement}
              className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs"
            >
              Add entitlement
            </button>
          </div>
          {userEntitlements.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Load a user to manage entitlements.
            </p>
          )}
          <div className="space-y-4">
            {userEntitlements.map((entry, index) => (
              <div
                key={`${entry.entitlement_key}-${index}`}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <label className="text-sm text-gray-700 dark:text-gray-300">
                    Entitlement key
                    <input
                      type="text"
                      value={entry.entitlement_key}
                      onChange={(e) =>
                        updateUserEntitlementField(index, 'entitlement_key', e.target.value)
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    />
                  </label>
                  <label className="text-sm text-gray-700 dark:text-gray-300">
                    Status
                    <select
                      value={entry.status}
                      onChange={(e) =>
                        updateUserEntitlementField(index, 'status', e.target.value)
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    >
                      <option value="active">Active</option>
                      <option value="grace">Grace</option>
                      <option value="expired">Expired</option>
                      <option value="revoked">Revoked</option>
                    </select>
                  </label>
                  <label className="text-sm text-gray-700 dark:text-gray-300">
                    Expires at
                    <input
                      type="datetime-local"
                      value={entry.expires_at}
                      onChange={(e) =>
                        updateUserEntitlementField(index, 'expires_at', e.target.value)
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    />
                  </label>
                  <div className="flex items-end">
                    <button
                      onClick={() => saveUserEntitlement(entry)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
                      disabled={savingKey === `user-entitlement-${entry.entitlement_key}`}
                    >
                      {savingKey === `user-entitlement-${entry.entitlement_key}`
                        ? 'Saving...'
                        : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 dark:text-white">Notes overrides</h3>
            <button
              onClick={addUserOverride}
              className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs"
            >
              Add override
            </button>
          </div>
          {userOverrides.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Load a user to manage overrides.
            </p>
          )}
          <div className="space-y-4">
            {userOverrides.map((entry, index) => (
              <div
                key={`${entry.entitlement_key}-${index}`}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <label className="text-sm text-gray-700 dark:text-gray-300">
                    Entitlement key
                    <input
                      type="text"
                      value={entry.entitlement_key}
                      onChange={(e) =>
                        updateUserOverrideField(index, 'entitlement_key', e.target.value)
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    />
                  </label>
                  <label className="text-sm text-gray-700 dark:text-gray-300">
                    Storage override (MB)
                    <input
                      type="number"
                      value={entry.notes_storage_mb_override}
                      onChange={(e) =>
                        updateUserOverrideField(index, 'notes_storage_mb_override', e.target.value)
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    />
                  </label>
                  <label className="text-sm text-gray-700 dark:text-gray-300">
                    Upload override (MB)
                    <input
                      type="number"
                      value={entry.notes_max_upload_mb_override}
                      onChange={(e) =>
                        updateUserOverrideField(index, 'notes_max_upload_mb_override', e.target.value)
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    />
                  </label>
                  <label className="text-sm text-gray-700 dark:text-gray-300">
                    Media sync
                    <select
                      value={entry.notes_media_sync_enabled_override}
                      onChange={(e) =>
                        updateUserOverrideField(index, 'notes_media_sync_enabled_override', e.target.value)
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    >
                      <option value="inherit">Inherit</option>
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </select>
                  </label>
                  <label className="text-sm text-gray-700 dark:text-gray-300">
                    Large media
                    <select
                      value={entry.notes_large_media_enabled_override}
                      onChange={(e) =>
                        updateUserOverrideField(index, 'notes_large_media_enabled_override', e.target.value)
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                    >
                      <option value="inherit">Inherit</option>
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </select>
                  </label>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => saveUserOverride(entry)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
                    disabled={savingKey === `user-override-${entry.entitlement_key}`}
                  >
                    {savingKey === `user-override-${entry.entitlement_key}`
                      ? 'Saving...'
                      : 'Save'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
